/** GERP 저장품 추가발주(184) — 레거시 appr_edit_forms_supplies_gerp */
export const INVENTORY_REPLENISHMENT_TYPE = 184;

export const isInventoryReplenishmentType = (apvType: number): boolean =>
  apvType === INVENTORY_REPLENISHMENT_TYPE;

export const INVENTORY_REPLENISHMENT_MISSING_MESSAGE =
  '데이터를 로드하지 않았습니다. 조회 조건 선택 후 확인 버튼을 누르세요.';

/** GERP 미상신 결재 건 — 키는 GERP 응답 그대로 */
export type InventoryReplenishmentCandidate = {
  sysId: string;
  aprvId: string;
  aprvTypcd: string;
  title?: string | null;
  docTit?: string | null;
};

type Value = string | number | null | undefined;

/** GERP 184 발주 라인(FESPPODT) — 키는 GERP 응답 그대로 */
export type InventoryReplenishmentLine = {
  itemCd?: Value;
  itemNm?: Value;
  itemQty?: Value;
  itemPrice?: Value;
  itemAmt?: Value;
  vdCd?: Value;
  vdNm?: Value;
  unitCd?: Value;
  cur?: Value;
  sizeCd?: Value;
  colorCd?: Value;
};

export type InventoryReplenishmentCard = {
  aprvId?: string | null;
  poId?: string | null;
  docTit?: string | null;
  lines?: InventoryReplenishmentLine[] | null;
};

export type InventoryReplenishmentDraftSource = {
  code: number;
  attributes: Record<string, unknown>;
  display: {
    candidates: InventoryReplenishmentCandidate[] | null;
    selection: string[] | null;
    subject: string | null;
    content: { cards?: InventoryReplenishmentCard[] | null } | null;
  };
};

/** 작성 중 고른 건 1개 */
export type InventoryReplenishmentItem = {
  /** GERP 결재 건(ESGMSTR.APRV_ID) */
  aprvId: string;
  /** 레거시 data-add_doctit — 건 제목(DOC_TIT) */
  docTit: string;
  lines: InventoryReplenishmentLine[];
};

/** 저장 attributes 와 같은 모양 */
export type InventoryReplenishmentState = {
  systemId: string;
  approvalTypeCode: string;
  /** 고른 건, 쉼표로 이음 */
  approvalId: string;
  /** 작성 시점 카드 HTML */
  content: string;
};

/** 레거시 콤보 문구 — 건 제목(DOC_TIT) */
export const buildInventoryReplenishmentLabel = (
  c: InventoryReplenishmentCandidate,
) => c.docTit?.trim() || c.title?.trim() || c.aprvId;

/** 레거시 change_title_content — 고른 순서의 첫 건 기준, "외" 앞 공백 없음 */
export const buildInventoryReplenishmentSubject = (
  docTits: string[],
  t: (key: string, options?: Record<string, unknown>) => string,
): string => {
  if (!docTits.length) return '';
  const [first] = docTits;
  if (docTits.length === 1)
    return t('저장품 추가 발주({{docTit}})', { docTit: first });
  return t('저장품 추가 발주({{docTit}}외 {{count}}건)', {
    docTit: first,
    count: docTits.length - 1,
  });
};

/** 레거시 본문 — 고를 때마다 덮어씀 */
export const buildInventoryReplenishmentEditorContent = (
  docTits: string[],
  t: (key: string) => string,
): string =>
  `<p>&nbsp;</p><p>${t('추가')} : ${docTits.join(
    ',',
  )}&nbsp;</p><p>&nbsp;</p><p>&nbsp;</p>`;

const nonEmpty = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() ? v : null;

export const parseInventoryReplenishmentAttributes = (
  attributes: Record<string, unknown> | null | undefined,
): InventoryReplenishmentState | null => {
  const content = nonEmpty(attributes?.content);
  if (!attributes || !content) return null;
  return {
    systemId: nonEmpty(attributes.systemId) ?? '',
    approvalTypeCode: nonEmpty(attributes.approvalTypeCode) ?? '',
    approvalId: nonEmpty(attributes.approvalId) ?? '',
    content,
  };
};

export const parseInventoryReplenishmentContent = (
  attributes: Record<string, unknown> | null | undefined,
): string | null => nonEmpty(attributes?.content);

export const buildInventoryReplenishmentAttributes = (
  state: InventoryReplenishmentState | null | undefined,
) =>
  state
    ? {
        systemId: state.systemId,
        approvalTypeCode: state.approvalTypeCode,
        approvalId: state.approvalId,
        content: state.content,
      }
    : null;

export const checkInventoryReplenishmentBeforeSave = (
  state: InventoryReplenishmentState | null | undefined,
): string | null =>
  state?.content && state.approvalId
    ? null
    : INVENTORY_REPLENISHMENT_MISSING_MESSAGE;

export const inventoryReplenishmentKey = (
  state: InventoryReplenishmentState | null | undefined,
) => state?.approvalId ?? '';
